import { z } from "zod";

export const BLANK_ADDRESS = {
  street1: "",
  street2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
  latitude: "",
  longitude: "",
  info: null,
  is_primary: false,
};

// The fields an address needs before it can be saved or marked primary.
export const REQUIRED_ADDRESS_FIELDS = [
  ["street1", "Street 1"],
  ["city", "City"],
  ["region", "Region"],
  ["postal_code", "Postal Code"],
  ["country", "Country"],
];

export const isAddressComplete = address =>
  REQUIRED_ADDRESS_FIELDS.every(([field]) => address[field]?.trim());

const toText = value => (value == null ? "" : String(value));

const isNumeric = value => value !== "" && !Number.isNaN(Number(value));

/**
 * A stored address as form values. The inputs hold text, so the coordinates
 * load as strings and `toPayloadAddress` converts them back.
 */
export const toFormAddress = (address, primaryAddressId = null) => ({
  id: address.id,
  street1: address.street1 ?? "",
  street2: address.street2 ?? "",
  city: address.city ?? "",
  region: address.region ?? "",
  postal_code: address.postal_code ?? "",
  country: address.country ?? "",
  latitude: toText(address.latitude),
  longitude: toText(address.longitude),
  info: address.info ?? null,
  is_primary: address.id === primaryAddressId,
});

export const addressSchema = z
  .object({
    id: z.number().optional(),
    street1: z.string(),
    street2: z.string(),
    city: z.string(),
    region: z.string(),
    postal_code: z.string(),
    // The country picker stores null when cleared.
    country: z.string().nullable(),
    latitude: z.string().trim(),
    longitude: z.string().trim(),
    info: z.any(),
    is_primary: z.boolean(),
  })
  .superRefine((address, ctx) => {
    REQUIRED_ADDRESS_FIELDS.forEach(([field, label]) => {
      if (!address[field]?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${label} is required`,
        });
      }
    });

    const { latitude, longitude } = address;
    if (Boolean(latitude) !== Boolean(longitude)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [latitude ? "longitude" : "latitude"],
        message: "Enter both latitude and longitude",
      });
    }
    if (latitude && !isNumeric(latitude)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["latitude"],
        message: "Latitude must be a number",
      });
    }
    if (longitude && !isNumeric(longitude)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["longitude"],
        message: "Longitude must be a number",
      });
    }
  });

const toCoordinate = value => (value.trim() ? Number(value) : null);

/** An address row as the request body expects it. */
export const toPayloadAddress = ({
  id,
  street1,
  street2,
  city,
  region,
  postal_code,
  country,
  latitude,
  longitude,
  info,
  is_primary,
}) => ({
  ...(id != null && { id }),
  street1: street1.trim(),
  street2: street2.trim(),
  city: city.trim(),
  region: region.trim(),
  postal_code: postal_code.trim(),
  country,
  latitude: toCoordinate(latitude),
  longitude: toCoordinate(longitude),
  info,
  is_primary,
});
